import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  X, Upload, Trash2, ChevronDown, ChevronRight,
  Plus, FileType, Check, AlertCircle, Loader2,
  FolderPlus, ArrowLeft, Copy, Eye, EyeOff,
  Pencil, AlertTriangle,
} from 'lucide-react';
import { useFontStore, type PendingFontFile } from '../../stores/fontStore';
import type { CustomFont } from '../../lib/fontLoader';
import { supabase } from '../../lib/supabase';
import { FontServerSync } from './FontServerSync';
import { FontMappingsTab } from './FontMappingsTab';
import { ProjectFontsTab } from './ProjectFontsTab';

interface FontManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const WEIGHT_OPTIONS = [
  { value: '100', label: 'Thin (100)' },
  { value: '200', label: 'Extra Light (200)' },
  { value: '300', label: 'Light (300)' },
  { value: '400', label: 'Regular (400)' },
  { value: '500', label: 'Medium (500)' },
  { value: '600', label: 'Semi Bold (600)' },
  { value: '700', label: 'Bold (700)' },
  { value: '800', label: 'Extra Bold (800)' },
  { value: '900', label: 'Black (900)' },
];

export function FontManager({ isOpen, onClose }: FontManagerProps) {
  const {
    familyName,
    pendingFiles,
    isUploading,
    uploadProgress,
    uploadedCount,
    totalCount,
    setFamilyName,
    addFiles,
    removeFile,
    updateFileMeta,
    clearPendingFiles,
    uploadFamily,
    deleteFont,
    deleteFontFamily,
    refreshFonts,
    fetchAllFonts,
    setFontFamilyActive,
    updateFont,
    renameFamily,
  } = useFontStore();

  const [view, setView] = useState<'list' | 'upload' | 'mappings' | 'project'>('list');
  const [addToExistingFamily, setAddToExistingFamily] = useState(false);
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
  const [uploadResult, setUploadResult] = useState<{ success: number; failed: number; duplicates: number } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ type: 'font' | 'family'; id: string; name: string } | null>(null);
  // Manager-local full list (includes inactive). Kept separate from store.fonts so
  // FontPicker / FontMappingsTab continue to see only active fonts.
  const [allFonts, setAllFonts] = useState<CustomFont[]>([]);
  const [togglingFamily, setTogglingFamily] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Edit variant modal state
  const [editingFont, setEditingFont] = useState<CustomFont | null>(null);
  const [editWeight, setEditWeight] = useState('400');
  const [editStyle, setEditStyle] = useState('normal');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editPostscript, setEditPostscript] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Rename family modal state
  const [renamingFamily, setRenamingFamily] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  // Server font manifest for expectations panel
  const [serverManifest, setServerManifest] = useState<{ fileName: string; postscriptName: string }[]>([]);

  const reloadAll = useCallback(async () => {
    const all = await fetchAllFonts();
    setAllFonts(all);
  }, [fetchAllFonts]);

  const loadServerManifest = useCallback(async () => {
    const { data } = await supabase
      .from('cs_server_fonts')
      .select('file_name, postscript_name')
      .order('postscript_name');
    if (data) {
      setServerManifest(data.map((r: any) => ({ fileName: r.file_name, postscriptName: r.postscript_name })));
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      refreshFonts();
      reloadAll();
      loadServerManifest();
      setView((prev) => (prev === 'upload' ? 'list' : prev));
      setUploadResult(null);
      clearPendingFiles();
    }
  }, [isOpen]);

  const handleToggleFamilyActive = async (family: string, nextActive: boolean) => {
    setTogglingFamily(family);
    try {
      await setFontFamilyActive(family, nextActive);
      await reloadAll();
    } finally {
      setTogglingFamily(null);
    }
  };

  const handleEditFont = (font: CustomFont) => {
    setEditingFont(font);
    setEditWeight(font.fontWeight);
    setEditStyle(font.fontStyle);
    setEditDisplayName(font.displayName);
    setEditPostscript(
      (font.postscriptName && font.postscriptName.trim())
        || (font.fileName ? font.fileName.replace(/\.(woff2|woff|ttf|otf)$/i, '') : '')
    );
  };

  const handleSaveEdit = async () => {
    if (!editingFont) return;
    setEditSaving(true);
    try {
      await updateFont(editingFont.id, {
        fontWeight: editWeight,
        fontStyle: editStyle,
        displayName: editDisplayName,
        postscriptName: editPostscript.trim(),
      });
      await reloadAll();
      setEditingFont(null);
      setEditPostscript('');
    } finally {
      setEditSaving(false);
    }
  };

  const handleRenameFamily = (family: string) => {
    setRenamingFamily(family);
    setRenameValue(family);
  };

  const handleSaveRename = async () => {
    if (!renamingFamily) return;
    setRenameSaving(true);
    try {
      await renameFamily(renamingFamily, renameValue);
      await reloadAll();
      setRenamingFamily(null);
    } finally {
      setRenameSaving(false);
    }
  };

  const fontFamilies = useMemo(() => {
    const map = new Map<string, CustomFont[]>();
    for (const font of allFonts) {
      const list = map.get(font.fontFamily) || [];
      list.push(font);
      map.set(font.fontFamily, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [allFonts]);

  const toggleFamily = (family: string) => {
    setExpandedFamilies(prev => {
      const next = new Set(prev);
      if (next.has(family)) next.delete(family);
      else next.add(family);
      return next;
    });
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.add('border-cyan-400', 'bg-cyan-500/10');
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove('border-cyan-400', 'bg-cyan-500/10');
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove('border-cyan-400', 'bg-cyan-500/10');
    }
    if (e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
      e.target.value = '';
    }
  }, [addFiles]);

  const handleUpload = async () => {
    const result = await uploadFamily();
    setUploadResult(result);
  };

  const handleDone = () => {
    setAddToExistingFamily(false);
    setView('list');
    setUploadResult(null);
    clearPendingFiles();
  };

  const handleAddVariantsToFamily = (family: string) => {
    clearPendingFiles();
    setFamilyName(family);
    setUploadResult(null);
    setAddToExistingFamily(true);
    setView('upload');
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    if (confirmDelete.type === 'family') {
      await deleteFontFamily(confirmDelete.id);
    } else {
      await deleteFont(confirmDelete.id);
    }
    await reloadAll();
    setConfirmDelete(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-xl w-[720px] max-h-[85vh] flex flex-col shadow-2xl relative">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            {view === 'upload' && (
              <button
                onClick={handleDone}
                className="p-1 rounded hover:bg-white/10 text-white/60 hover:text-white transition-colors"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <FileType size={20} className="text-cyan-400" />
            <h2 className="text-lg font-semibold text-white">
              {view === 'upload'
                ? `Upload: ${familyName || 'New Family'}`
                : 'Font Manager'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {view !== 'upload' && (
          <div className="flex items-center gap-1 px-6 pt-3 border-b border-white/[0.06]">
            <TabButton active={view === 'list'} onClick={() => setView('list')}>
              Families
            </TabButton>
            <TabButton active={view === 'mappings'} onClick={() => setView('mappings')}>
              SVG Mappings
            </TabButton>
            <TabButton active={view === 'project'} onClick={() => setView('project')}>
              Project Fonts
            </TabButton>
          </div>
        )}

        <div className="flex-1 overflow-y-auto sidebar-scroll relative">
          {view === 'list' && <FontServerSync />}
          {view === 'list' && (
            <FamilyListView
              fontFamilies={fontFamilies}
              fonts={allFonts}
              expandedFamilies={expandedFamilies}
              toggleFamily={toggleFamily}
              onAddFamily={() => setView('upload')}
              onAddVariants={handleAddVariantsToFamily}
              onDeleteFamily={(family) => setConfirmDelete({ type: 'family', id: family, name: family })}
              onDeleteFont={(font) => setConfirmDelete({ type: 'font', id: font.id, name: font.displayName })}
              onToggleFamilyActive={handleToggleFamilyActive}
              togglingFamily={togglingFamily}
              onEditFont={handleEditFont}
              onRenameFamily={handleRenameFamily}
            />
          )}
          {view === 'mappings' && <FontMappingsTab />}
          {view === 'project' && <ProjectFontsTab onGoToMappings={() => setView('mappings')} />}
          {view === 'upload' && (
            <UploadView
              familyName={familyName}
              setFamilyName={setFamilyName}
              pendingFiles={pendingFiles}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
              uploadedCount={uploadedCount}
              totalCount={totalCount}
              uploadResult={uploadResult}
              addToExistingFamily={addToExistingFamily}
              dropZoneRef={dropZoneRef}
              fileInputRef={fileInputRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onFileInput={handleFileInput}
              onRemoveFile={removeFile}
              onUpdateFileMeta={updateFileMeta}
              onClearFiles={clearPendingFiles}
              serverManifest={serverManifest}
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
          {view === 'upload' ? (
            <>
              {uploadResult ? (
                <button
                  onClick={handleDone}
                  className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors text-sm font-medium"
                >
                  Done
                </button>
              ) : (
                <>
                  <button
                    onClick={handleDone}
                    disabled={isUploading}
                    className="px-4 py-2 text-white/60 hover:text-white transition-colors text-sm disabled:opacity-30"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={isUploading || !familyName.trim() || pendingFiles.length === 0}
                    className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-500 transition-colors text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={14} />
                        Upload {pendingFiles.length} {pendingFiles.length === 1 ? 'File' : 'Files'}
                      </>
                    )}
                  </button>
                </>
              )}
            </>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 text-white/60 hover:text-white transition-colors text-sm"
            >
              Close
            </button>
          )}
        </div>

        {confirmDelete && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl z-10">
            <div className="bg-[#222] border border-white/10 rounded-lg p-6 max-w-sm mx-4 shadow-xl">
              <h3 className="text-white font-medium mb-2">
                Delete {confirmDelete.type === 'family' ? 'Font Family' : 'Font Variant'}?
              </h3>
              <p className="text-white/50 text-sm mb-4">
                {confirmDelete.type === 'family'
                  ? `This will delete all variants of "${confirmDelete.name}" and remove the font files from storage. This cannot be undone.`
                  : `This will delete "${confirmDelete.name}" and remove the font file from storage. This cannot be undone.`}
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="px-3 py-1.5 text-white/60 hover:text-white text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 text-sm font-medium transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {editingFont && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl z-10">
            <div className="bg-[#222] border border-white/10 rounded-lg p-6 max-w-sm mx-4 shadow-xl">
              <h3 className="text-white font-medium mb-4">Edit Variant</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-white/50 mb-1 flex items-center justify-between">
                    <span>PostScript Name</span>
                    {editPostscript.trim() && serverManifest.length > 0 && (
                      serverManifest.some((e) => e.postscriptName === editPostscript.trim()) ? (
                        <span className="text-emerald-400 flex items-center gap-1 text-[10px]">
                          <Check size={10} /> matches server
                        </span>
                      ) : (
                        <span className="text-amber-400 flex items-center gap-1 text-[10px]">
                          <AlertTriangle size={10} /> not on server
                        </span>
                      )
                    )}
                  </label>
                  <input
                    value={editPostscript}
                    onChange={(e) => setEditPostscript(e.target.value)}
                    placeholder="e.g. BebasNeuePro-Bold"
                    className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm text-white font-mono focus:outline-none focus:border-cyan-500/50"
                  />
                  {serverManifest.length > 0 && (
                    <div className="mt-1.5">
                      <label className="block text-[10px] text-white/40 mb-0.5">Pick from server manifest</label>
                      <select
                        value=""
                        onChange={(e) => {
                          if (e.target.value) setEditPostscript(e.target.value);
                        }}
                        className="w-full px-2 py-1 bg-white/5 border border-white/10 rounded text-xs text-white focus:outline-none focus:border-cyan-500/50"
                        style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
                      >
                        <option value="" style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}>
                          — Select server name —
                        </option>
                        {serverManifest.map((e) => (
                          <option
                            key={e.postscriptName}
                            value={e.postscriptName}
                            style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
                          >
                            {e.postscriptName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1">Display Name</label>
                  <input
                    value={editDisplayName}
                    onChange={(e) => setEditDisplayName(e.target.value)}
                    className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm text-white focus:outline-none focus:border-cyan-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1">Weight</label>
                  <select
                    value={editWeight}
                    onChange={(e) => setEditWeight(e.target.value)}
                    className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm text-white focus:outline-none focus:border-cyan-500/50"
                  >
                    {WEIGHT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-white/50 mb-1">Style</label>
                  <select
                    value={editStyle}
                    onChange={(e) => setEditStyle(e.target.value)}
                    className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm text-white focus:outline-none focus:border-cyan-500/50"
                  >
                    <option value="normal">Normal</option>
                    <option value="italic">Italic</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => { setEditingFont(null); setEditPostscript(''); }}
                  className="px-3 py-1.5 text-white/60 hover:text-white text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={editSaving}
                  className="px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 text-sm font-medium transition-colors disabled:opacity-40"
                >
                  {editSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {renamingFamily && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl z-10">
            <div className="bg-[#222] border border-white/10 rounded-lg p-6 max-w-sm mx-4 shadow-xl">
              <h3 className="text-white font-medium mb-4">Rename Family</h3>
              <div>
                <label className="block text-xs text-white/50 mb-1">Family Name</label>
                <input
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded text-sm text-white focus:outline-none focus:border-cyan-500/50"
                />
                <p className="text-[10px] text-amber-400/80 mt-1 flex items-center gap-1">
                  <AlertTriangle size={10} />
                  Renaming updates all variants. Existing elements referencing the old name will need updating.
                </p>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setRenamingFamily(null)}
                  className="px-3 py-1.5 text-white/60 hover:text-white text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRename}
                  disabled={renameSaving || !renameValue.trim() || renameValue.trim() === renamingFamily}
                  className="px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 text-sm font-medium transition-colors disabled:opacity-40"
                >
                  {renameSaving ? 'Saving...' : 'Rename'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative px-3 py-2 text-xs font-medium transition-colors ${
        active
          ? 'text-cyan-400'
          : 'text-white/50 hover:text-white/80'
      }`}
    >
      {children}
      {active && (
        <span className="absolute left-0 right-0 -bottom-px h-px bg-cyan-400" />
      )}
    </button>
  );
}

function FamilyListView({
  fontFamilies,
  fonts,
  expandedFamilies,
  toggleFamily,
  onAddFamily,
  onAddVariants,
  onDeleteFamily,
  onDeleteFont,
  onToggleFamilyActive,
  togglingFamily,
  onEditFont,
  onRenameFamily,
}: {
  fontFamilies: [string, CustomFont[]][];
  fonts: CustomFont[];
  expandedFamilies: Set<string>;
  toggleFamily: (family: string) => void;
  onAddFamily: () => void;
  onAddVariants: (family: string) => void;
  onDeleteFamily: (family: string) => void;
  onDeleteFont: (font: CustomFont) => void;
  onToggleFamilyActive: (family: string, nextActive: boolean) => void | Promise<void>;
  togglingFamily: string | null;
  onEditFont: (font: CustomFont) => void;
  onRenameFamily: (family: string) => void;
}) {
  if (fontFamilies.length === 0) {
    return (
      <div className="p-6 text-center py-12">
        <FileType size={48} className="mx-auto text-white/20 mb-4" />
        <p className="text-white/50 text-sm mb-2">No custom fonts uploaded yet</p>
        <p className="text-white/30 text-xs mb-6">Upload your brand fonts to use them in designs</p>
        <button
          onClick={onAddFamily}
          className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors text-sm font-medium"
        >
          <Plus size={16} className="inline mr-2" />
          Add Font Family
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <p className="text-white/50 text-sm">
          {fontFamilies.length} {fontFamilies.length === 1 ? 'family' : 'families'} &middot; {fonts.length} {fonts.length === 1 ? 'variant' : 'variants'}
        </p>
        <button
          onClick={onAddFamily}
          className="px-3 py-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors text-sm font-medium flex items-center gap-1.5"
        >
          <FolderPlus size={14} />
          Add Family
        </button>
      </div>

      <div className="space-y-2">
        {fontFamilies.map(([family, familyFonts]) => {
          const familyIsActive = familyFonts.some((f) => f.isActive);
          const isToggling = togglingFamily === family;
          return (
          <div
            key={family}
            className={`border rounded-lg overflow-hidden transition-colors ${
              familyIsActive ? 'border-white/10' : 'border-white/5 bg-white/[0.02]'
            }`}
          >
            <div
              className="flex items-center justify-between px-4 py-3 hover:bg-white/5 cursor-pointer transition-colors"
              onClick={() => toggleFamily(family)}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {expandedFamilies.has(family) ? (
                  <ChevronDown size={16} className="text-white/40" />
                ) : (
                  <ChevronRight size={16} className="text-white/40" />
                )}
                <span
                  className={`font-medium truncate ${
                    familyIsActive ? 'text-white' : 'text-white/40'
                  }`}
                  style={{ fontFamily: `'${family}', sans-serif` }}
                >
                  {family}
                </span>
                <span className="text-white/40 text-xs ml-2 flex-shrink-0">
                  {familyFonts.length} {familyFonts.length === 1 ? 'variant' : 'variants'}
                </span>
                {!familyIsActive && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-amber-400/80 bg-amber-500/10 px-1.5 py-0.5 rounded flex-shrink-0">
                    Disabled
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddVariants(family);
                  }}
                  className="p-1 rounded hover:bg-white/10 text-white/30 hover:text-cyan-400 transition-colors"
                  title="Add variants to this family"
                >
                  <Plus size={12} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRenameFamily(family);
                  }}
                  className="p-1 rounded hover:bg-white/10 text-white/30 hover:text-cyan-400 transition-colors"
                  title="Rename family"
                >
                  <Pencil size={12} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFamilyActive(family, !familyIsActive);
                  }}
                  disabled={isToggling}
                  className={`p-1 rounded transition-colors disabled:opacity-40 ${
                    familyIsActive
                      ? 'hover:bg-white/10 text-white/50 hover:text-white'
                      : 'hover:bg-cyan-500/20 text-amber-400/80 hover:text-cyan-400'
                  }`}
                  title={familyIsActive ? 'Deactivate family (hide from pickers)' : 'Activate family'}
                >
                  {isToggling ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : familyIsActive ? (
                    <Eye size={14} />
                  ) : (
                    <EyeOff size={14} />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteFamily(family);
                  }}
                  className="p-1 rounded hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-colors"
                  title="Delete entire family"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            {expandedFamilies.has(family) && (
              <div className="border-t border-white/5">
                {familyFonts.map(font => (
                  <div
                    key={font.id}
                    className="flex items-center justify-between px-4 py-2.5 pl-10 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="flex flex-col gap-0.5 w-28 flex-shrink-0">
                        <span className="text-[10px] uppercase tracking-wider text-white/50 font-semibold">
                          {font.displayName}
                        </span>
                        <span className="text-[10px] text-white/30">
                          {(font.fileSize / 1024).toFixed(0)} KB
                        </span>
                      </div>
                      <span
                        className="text-xl text-white/90 truncate"
                        style={{
                          fontFamily: `'${font.fontFamily}', sans-serif`,
                          fontWeight: font.fontWeight,
                          fontStyle: font.fontStyle,
                        }}
                        title={font.fileName}
                      >
                        AaBbCc 123
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => onEditFont(font)}
                        className="p-1 rounded hover:bg-white/10 text-white/30 hover:text-cyan-400 transition-colors"
                        title="Edit variant"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => onDeleteFont(font)}
                        className="p-1 rounded hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-colors"
                        title="Delete variant"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

function UploadView({
  familyName,
  setFamilyName,
  pendingFiles,
  isUploading,
  uploadProgress,
  uploadedCount,
  totalCount,
  uploadResult,
  addToExistingFamily,
  dropZoneRef,
  fileInputRef,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileInput,
  onRemoveFile,
  onUpdateFileMeta,
  onClearFiles,
  serverManifest,
}: {
  familyName: string;
  setFamilyName: (name: string) => void;
  pendingFiles: PendingFontFile[];
  isUploading: boolean;
  uploadProgress: number;
  uploadedCount: number;
  totalCount: number;
  uploadResult: { success: number; failed: number; duplicates: number } | null;
  addToExistingFamily: boolean;
  dropZoneRef: React.RefObject<HTMLDivElement>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  onUpdateFileMeta: (index: number, updates: Partial<Pick<PendingFontFile, 'weight' | 'style' | 'displayName'>>) => void;
  onClearFiles: () => void;
  serverManifest: { fileName: string; postscriptName: string }[];
}) {
  const serverPostscriptSet = useMemo(
    () => new Set(serverManifest.map((e) => e.postscriptName)),
    [serverManifest]
  );
  return (
    <div className="p-6 space-y-6">
      <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 flex items-center justify-between border-b border-white/[0.06]">
          <h4 className="text-xs font-medium text-white/70 flex items-center gap-1.5">
            <FileType size={12} className="text-cyan-400/60" />
            Server Expectations
          </h4>
          {serverManifest.length === 0 ? (
            <span className="text-[10px] text-white/30">manifest not loaded</span>
          ) : (
            <span className="text-[10px] text-white/40">
              {serverManifest.length} entries on server
            </span>
          )}
        </div>
        <div className="px-4 py-2.5">
          <p className="text-[10px] text-white/40 mb-2">
            The export packet references PostScript names below. When you upload, the file's PostScript name (derived from the filename) must match one of these entries for the export server to find it.
          </p>
          {serverManifest.length === 0 ? (
            <div className="text-[10px] text-white/30 italic">
              No server manifest data loaded. Uploaded fonts will still work, but server matching cannot be validated.
            </div>
          ) : (
            <div className="max-h-[120px] overflow-y-auto sidebar-scroll space-y-0.5 font-mono">
              {serverManifest.map((e) => (
                <div key={e.postscriptName} className="flex items-center justify-between text-[10px]">
                  <span className="text-white/70">{e.postscriptName}</span>
                  <span className="text-white/30 truncate ml-2">{e.fileName}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/70 mb-2">Font Family Name</label>
        {addToExistingFamily && (
          <div className="mb-2 px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-sm">
            Adding variants to: <span className="font-semibold">{familyName}</span>
          </div>
        )}
        <input
          type="text"
          value={familyName}
          onChange={(e) => setFamilyName(e.target.value)}
          placeholder="e.g., Trade Gothic LT Std"
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-cyan-500/50 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isUploading || addToExistingFamily}
        />
        <p className="text-white/30 text-xs mt-1">
          {addToExistingFamily
            ? 'Family name is locked while adding variants. Cancel and return to the family list to add a different family.'
            : 'This is the CSS font-family name. Must match exactly how the font is referenced in SVG files.'}
        </p>
      </div>

      <div
        ref={dropZoneRef}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center transition-colors cursor-pointer hover:border-white/30"
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload size={32} className="mx-auto text-white/30 mb-3" />
        <p className="text-white/60 text-sm mb-1">
          Drag &amp; drop font files here, or click to browse
        </p>
        <p className="text-white/30 text-xs">
          Supports .woff2, .woff, .ttf, .otf — Select multiple files at once
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".woff2,.woff,.ttf,.otf"
          onChange={onFileInput}
          className="hidden"
        />
      </div>

      {pendingFiles.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-white/70">
              {pendingFiles.length} {pendingFiles.length === 1 ? 'file' : 'files'} ready
            </h3>
            {!isUploading && !uploadResult && (
              <button onClick={onClearFiles} className="text-xs text-white/40 hover:text-white/60 transition-colors">
                Clear all
              </button>
            )}
          </div>

          <div className="border border-white/10 rounded-lg overflow-hidden">
            <div className="grid grid-cols-[1fr_120px_100px_80px_40px] gap-2 px-4 py-2 bg-white/5 text-xs text-white/40 font-medium">
              <span>File</span>
              <span>Weight</span>
              <span>Style</span>
              <span>Status</span>
              <span></span>
            </div>

            <div className="max-h-[300px] overflow-y-auto sidebar-scroll">
              {pendingFiles.map((pf, index) => (
                <PendingFileRow
                  key={`${pf.fileName}-${index}`}
                  pf={pf}
                  index={index}
                  isUploading={isUploading}
                  onRemove={onRemoveFile}
                  onUpdateMeta={onUpdateFileMeta}
                  serverPostscriptSet={serverPostscriptSet}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {isUploading && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-white/60">Uploading {uploadedCount} of {totalCount}...</span>
            <span className="text-sm text-cyan-400 font-medium">{uploadProgress}%</span>
          </div>
          <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-cyan-500 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {uploadResult && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <h4 className="text-sm font-medium text-white mb-2">Upload Complete</h4>
          <div className="flex items-center gap-4 text-sm">
            {uploadResult.success > 0 && (
              <span className="text-green-400">
                <Check size={14} className="inline mr-1" />
                {uploadResult.success} uploaded
              </span>
            )}
            {uploadResult.duplicates > 0 && (
              <span className="text-yellow-400">
                <Copy size={14} className="inline mr-1" />
                {uploadResult.duplicates} duplicates skipped
              </span>
            )}
            {uploadResult.failed > 0 && (
              <span className="text-red-400">
                <AlertCircle size={14} className="inline mr-1" />
                {uploadResult.failed} failed
              </span>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

function PendingFileRow({
  pf,
  index,
  isUploading,
  onRemove,
  onUpdateMeta,
  serverPostscriptSet,
}: {
  pf: PendingFontFile;
  index: number;
  isUploading: boolean;
  onRemove: (index: number) => void;
  onUpdateMeta: (index: number, updates: Partial<Pick<PendingFontFile, 'weight' | 'style' | 'displayName'>>) => void;
  serverPostscriptSet: Set<string>;
}) {
  return (
    <div className="grid grid-cols-[1fr_120px_100px_80px_40px] gap-2 px-4 py-2 border-t border-white/5 items-center">
      <div className="flex flex-col gap-0.5 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <FileType size={14} className="text-white/30 flex-shrink-0" />
          <span className="text-white/80 text-xs truncate" title={pf.fileName}>{pf.fileName}</span>
        </div>
        {(() => {
          const derived = pf.fileName.replace(/\.(woff2|woff|ttf|otf)$/i, '').trim();
          const matches = serverPostscriptSet.has(derived);
          return (
            <div className="flex items-center gap-1 pl-5 text-[9px] font-mono">
              <span className="text-white/30">ps:</span>
              <span className="text-white/50 truncate">{derived}</span>
              {matches ? (
                <Check size={9} className="text-emerald-400 flex-shrink-0" />
              ) : (
                <AlertTriangle size={9} className="text-amber-400 flex-shrink-0" />
              )}
            </div>
          );
        })()}
      </div>

      <select
        value={pf.weight}
        onChange={(e) => {
          const opt = WEIGHT_OPTIONS.find(o => o.value === e.target.value);
          const label = opt?.label.split(' (')[0] || 'Regular';
          onUpdateMeta(index, {
            weight: e.target.value,
            displayName: pf.style === 'italic' ? `${label} Italic` : label,
          });
        }}
        disabled={isUploading}
        className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white/80 focus:outline-none focus:border-cyan-500/50"
      >
        {WEIGHT_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      <select
        value={pf.style}
        onChange={(e) => {
          const weightLabel = WEIGHT_OPTIONS.find(o => o.value === pf.weight)?.label.split(' (')[0] || 'Regular';
          onUpdateMeta(index, {
            style: e.target.value,
            displayName: e.target.value === 'italic' ? `${weightLabel} Italic` : weightLabel,
          });
        }}
        disabled={isUploading}
        className="bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white/80 focus:outline-none focus:border-cyan-500/50"
      >
        <option value="normal">Normal</option>
        <option value="italic">Italic</option>
      </select>

      <div className="flex items-center gap-1">
        {pf.status === 'pending' && <span className="text-white/40 text-xs">Pending</span>}
        {pf.status === 'uploading' && <Loader2 size={12} className="text-cyan-400 animate-spin" />}
        {pf.status === 'success' && <Check size={14} className="text-green-400" />}
        {pf.status === 'error' && <span title={pf.error}><AlertCircle size={14} className="text-red-400" /></span>}
        {pf.status === 'duplicate' && <span title={pf.error}><Copy size={14} className="text-yellow-400" /></span>}
      </div>

      <button
        onClick={() => onRemove(index)}
        disabled={isUploading}
        className="p-1 rounded hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-colors disabled:opacity-30"
      >
        <X size={12} />
      </button>
    </div>
  );
}
